import { apolloClient } from '../lib/apollo';
import { getRefreshToken, setToken, setRefreshToken } from './tokenManager';
import { REFRESH_ACCESS_TOKEN } from '../graphql/auth/mutations';
import { logger } from './logger';

/**
 * Refreshes the access token using the refresh token
 * @returns Promise that resolves with the result of the refresh
 */
export const refreshAccessToken = async (): Promise<{
  success: boolean;
  newToken?: string;
  newRefreshToken?: string;
  error?: string;
  errorCode?: string;
}> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return { success: false, error: 'No refresh token available' };
  }

  try {
    const { data } = await apolloClient.mutate({
      mutation: REFRESH_ACCESS_TOKEN,
      variables: { data: { refreshToken } },
      context: {
        headers: {
          // No authorization header needed for refresh
        },
        // Mark this as a refresh operation to prevent errorLink from processing it
        skipRefresh: true,
      },
      // Use errorPolicy to prevent errorLink from handling errors
      errorPolicy: 'all',
    });

    // Check if we have a valid response with accessToken
    if (data?.refreshAccessToken?.accessToken) {
      const { accessToken, refreshToken: newRefreshToken } = data.refreshAccessToken;

      // Store the new tokens
      setToken(accessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }

      return {
        success: true,
        newToken: accessToken,
        newRefreshToken: newRefreshToken || refreshToken,
      };
    } else {
      // Return error from response — detect session eviction for the UX modal
      const errorMessage = data?.refreshAccessToken?.message || '';
      const isSessionEvicted = errorMessage.toLowerCase().includes('session evicted');
      return {
        success: false,
        error: errorMessage || 'Token refresh failed',
        ...(isSessionEvicted ? { errorCode: 'SESSION_EVICTED' } : {}),
      };
    }
  } catch (error) {
    logger.error({
      message: 'Token refresh failed',
      context: { error: error instanceof Error ? error.message : 'Unknown error' },
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Token refresh failed',
    };
  }
};
